import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Text, useTheme, mergeStyles } from '@fluentui/react';
import {
  Tooltip,
  Button,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  FluentProvider,
  makeStyles,
  TableColumnDefinition,
  tokens,
  webLightTheme,
} from '@fluentui/react-components';
import { ChevronDownRegular, ChevronRightRegular, InfoRegular } from '@fluentui/react-icons';
import Card from '../../common/card/Card';
import {
  ENTITY_STATUS_ACTIVE,
  ENTITY_STATUS_NOTICES_PENDING,
  ENTITY_STATUS_TERMINATED,
} from '../../../app/common/constants/EntityStatusConstants';
import {
  detailsContainerStyles,
  getMetricLabelStyles,
  getMetricRowStyles,
  getMetricValueStyles,
  getParentCompanyInfoStyles,
  getPrimaryLabelStyles,
  getPrimaryMetricStyles,
} from '../EntityTerminationStyles';
import { ICompanyLegalStructure } from '../types';
import { IEntityTerminationSubsidiary } from '../../../utils/types/IEntityTerminationSummary';
import { iconButtonStyles } from '../../../app/common/styles/CommonStyleObjects';

interface IEntityTerminationLegalStructureCardProps {
  loading?: boolean;
  data?: ICompanyLegalStructure;
}

const useStyles = makeStyles({
  scrollContainer: {
    overflowY: 'auto',
    position: 'relative',
    height: '100%',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerCell: {
    fontWeight: 600,
  },
  truncateText: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

const EntityTerminationLegalStructureCard = (
  props: IEntityTerminationLegalStructureCardProps,
): JSX.Element => {
  const { loading, data } = props;
  const [isExpanded, setIsExpanded] = React.useState(false);
  const styles = useStyles();
  const theme = useTheme();

  const primaryMetricStyles = getPrimaryMetricStyles(theme);
  const primaryLabelStyles = getPrimaryLabelStyles(theme);
  const metricValueStyles = getMetricValueStyles(theme);
  const metricLabelStyles = getMetricLabelStyles(theme);
  const parentCompanyInfoStyles = getParentCompanyInfoStyles(theme);

  const parentCompany = data?.directParentCompany;
  const isCompanyFractionallyOwned = data?.directParentOwnershipPercentage < 1;
  const childrenCompanies = data?.childrenCompanies || [];
  const terminatedSubsidiariesCount = data?.terminatedSubsidiariesCount || 0;
  const nonTerminatedSubsidiariesCount = data?.nonTerminatedSubsidiariesCount || 0;
  const noticesPendingSubsidiariesCount = data?.noticesPendingSubsidiariesCount || 0;
  const activeSubsidiariesCount = data?.activeSubsidiariesCount || 0;

  // In this context, an Active status is undesired and Terminated is the desired outcome
  const getEntityStatusColor = (status: string): string => {
    switch (status) {
      case ENTITY_STATUS_TERMINATED:
        return theme.palette.green;
      case ENTITY_STATUS_NOTICES_PENDING:
        return theme.palette.yellow;
      case ENTITY_STATUS_ACTIVE:
        return theme.palette.red;
      default:
        return theme.palette.neutralSecondary;
    }
  };

  const renderMetricRow = (value: number, label: string, borderColor: string): JSX.Element => (
    <div className={getMetricRowStyles(borderColor)}>
      <div className={metricValueStyles}>{value}</div>
      <div className={metricLabelStyles}>
        {label.split(' ').map((word, index) => (
          <React.Fragment key={word}>
            {word}
            {index < label.split(' ').length - 1 && <br />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderCompanyName = (item: IEntityTerminationSubsidiary): JSX.Element => {
    const fractionalOwnershipIndicator = item.directParentOwnershipPercentage < 1 ? '*' : '';

    return (
      <Tooltip withArrow content={item.entityLegalName} relationship="label">
        <span className={styles.truncateText}>
          {fractionalOwnershipIndicator} {item.companyCode} - {item.entityLegalName}
        </span>
      </Tooltip>
    );
  };

  const renderEntityStatus = (item: IEntityTerminationSubsidiary): JSX.Element => (
    <Tooltip withArrow content={item.entityStatus.fieldName} relationship="label">
      <span
        className={styles.truncateText}
        style={{ color: getEntityStatusColor(item.entityStatus.fieldName) }}
      >
        {item.entityStatus.fieldName}
      </span>
    </Tooltip>
  );

  const renderTerminationLifecycleStatus = (item: IEntityTerminationSubsidiary): JSX.Element => (
    <Tooltip
      withArrow
      content={item.terminationLifecycleStatus?.fieldName ?? '--'}
      relationship="label"
    >
      <span
        className={styles.truncateText}
        style={{ color: getEntityStatusColor(item.terminationLifecycleStatus?.fieldName ?? '') }}
      >
        {item.terminationLifecycleStatus?.fieldName ?? '--'}
      </span>
    </Tooltip>
  );

  const renderDirectParentCompanyName = (item: IEntityTerminationSubsidiary): JSX.Element => (
    <Tooltip withArrow content={item.directParentCompany.entityLegalName} relationship="label">
      <span className={styles.truncateText}>
        {item.directParentCompany.companyCode} - {item.directParentCompany.entityLegalName}
      </span>
    </Tooltip>
  );

  const renderOwnershipPercentage = (item: IEntityTerminationSubsidiary): JSX.Element => (
    <span className={styles.truncateText}>
      {(item.directParentOwnershipPercentage ?? 0).toLocaleString(undefined, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}
    </span>
  );

  const renderCountry = (item: IEntityTerminationSubsidiary): JSX.Element => (
    <Tooltip withArrow content={item.physicalCountryCode} relationship="label">
      <span className={styles.truncateText}>{item.physicalCountryCode}</span>
    </Tooltip>
  );

  const columns: TableColumnDefinition<IEntityTerminationSubsidiary>[] = [
    createTableColumn<IEntityTerminationSubsidiary>({
      columnId: 'company',
      renderHeaderCell: () => 'Company',
      renderCell: (item) => renderCompanyName(item),
    }),
    createTableColumn<IEntityTerminationSubsidiary>({
      columnId: 'entityStatus',
      renderHeaderCell: () => 'Legal Status',
      renderCell: (item) => renderEntityStatus(item),
    }),
    createTableColumn<IEntityTerminationSubsidiary>({
      columnId: 'terminationLifecycleStatus',
      renderHeaderCell: () => 'Termination Lifecycle Status',
      renderCell: (item) => renderTerminationLifecycleStatus(item),
    }),
    createTableColumn<IEntityTerminationSubsidiary>({
      columnId: 'directParent',
      renderHeaderCell: () => 'Direct Parent',
      renderCell: (item) => renderDirectParentCompanyName(item),
    }),
    createTableColumn<IEntityTerminationSubsidiary>({
      columnId: 'ownershipPercentage',
      renderHeaderCell: () => 'Ownership %',
      renderCell: (item) => renderOwnershipPercentage(item),
    }),
    createTableColumn<IEntityTerminationSubsidiary>({
      columnId: 'country',
      renderHeaderCell: () => 'Country',
      renderCell: (item) => renderCountry(item),
    }),
  ];

  const renderDetailsSection = (): JSX.Element | null => {
    if (!childrenCompanies) return null;

    return (
      <Stack className={mergeStyles({ marginTop: '20px' })}>
        <Button
          appearance="transparent"
          style={{
            marginLeft: 0,
            padding: 0,
            justifyContent: 'flex-start',
          }}
          icon={
            isExpanded ? (
              <ChevronDownRegular color={iconButtonStyles.color} />
            ) : (
              <ChevronRightRegular color={iconButtonStyles.color} />
            )
          }
          title={isExpanded ? `Collapse details` : `Expand details`}
          aria-label={isExpanded ? `Collapse details` : `Expand details`}
          aria-expanded={!isExpanded}
          onClick={(): void => {
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? `Collapse details` : `Expand details`}
        </Button>
        {isExpanded && (
          <div className={detailsContainerStyles} id="entity-details">
            <div className={styles.scrollContainer}>
              <FluentProvider theme={webLightTheme}>
                <DataGrid
                  items={childrenCompanies}
                  columns={columns}
                  resizableColumns
                  aria-label="Children companies list"
                  getRowId={(item) => item.companyCode}
                >
                  <DataGridHeader className={styles.stickyHeader}>
                    <DataGridRow>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell className={styles.headerCell}>
                          <span className={styles.truncateText}>{renderHeaderCell()}</span>
                        </DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody<IEntityTerminationSubsidiary>>
                    {({ item, rowId }) => (
                      <DataGridRow<IEntityTerminationSubsidiary> key={rowId}>
                        {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
              </FluentProvider>
            </div>
          </div>
        )}
      </Stack>
    );
  };

  const renderContent = (): JSX.Element => {
    if (!data) {
      return <Text>No data available</Text>;
    }

    return (
      <Stack>
        {/* Parent Company Information */}
        {/* Placeholder data until we pull in real data */}
        <div className={parentCompanyInfoStyles}>
          Parent: {isCompanyFractionallyOwned ? '*' : ''}
          {parentCompany?.companyCode} - {parentCompany?.entityLegalName} -{' '}
          {parentCompany?.entityStatus?.fieldName} - {parentCompany?.physicalCountryCode}
        </div>

        {/* Primary metric with visual hierarchy */}
        <Stack horizontal horizontalAlign="space-between">
          <Stack>
            <div className={primaryMetricStyles}>{nonTerminatedSubsidiariesCount}</div>
            <div className={primaryLabelStyles}>
              Non-Terminated
              <br />
              Subsidiaries
            </div>
          </Stack>
        </Stack>

        {/* Secondary metrics with horizontal layout */}
        <Stack
          horizontal
          wrap
          tokens={{ childrenGap: 16 }}
          className={mergeStyles({ marginTop: '20px', marginLeft: 10 })}
        >
          {renderMetricRow(
            terminatedSubsidiariesCount,
            'Terminated Subsidiaries',
            theme.palette.green,
          )}
          {renderMetricRow(
            noticesPendingSubsidiariesCount,
            'Notices Pending',
            theme.palette.yellow,
          )}
          {renderMetricRow(activeSubsidiariesCount, 'Active', theme.palette.red)}
        </Stack>

        {/* Expandable details section */}
        {renderDetailsSection()}
      </Stack>
    );
  };

  return (
    <Card
      title="Legal Structure"
      isLoading={!!loading}
      headingLevel={2}
      actionButton={
        <Tooltip
          withArrow
          content="Parent company name will appear with asterisk (*) if there is more than one parent company of the company in this review. Companies in the subsidiary list will also appear with asterisk (*) if they are owned by more than one company."
          relationship="label"
        >
          <Button
            appearance="subtle"
            size="small"
            aria-label="Asterisk explanation"
            icon={<InfoRegular color={iconButtonStyles.color} />}
          />
        </Tooltip>
      }
    >
      {renderContent()}
    </Card>
  );
};

export default EntityTerminationLegalStructureCard;
